import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { parseUnits } from 'viem'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrg } from '~/hooks/org'
import {
  CreateTransactionData,
  EarnDataPayload,
} from '~/hooks/smart-account-actions/createTransactionData.type'
import { getTokenDisplayForTransactionName } from '~/hooks/smart-account-actions/getTokenDisplayForTransactionName'
import { SwapTransactionParams } from '~/hooks/smart-account-actions/types'
import { useCreateTransaction } from '~/hooks/smart-account-actions/useCreateTransaction'
import { useNavigation } from '~/hooks/useNavigation'
import { useServerRequests } from '~/hooks/useRequests'
import {
  AuthOrg,
  BasicTransactionType,
  EarnAction,
  OrgSmartAccount,
  OrgTransaction,
} from '~/types'
import { Address } from '~/utils/address'
import { shortenAddress } from '~/utils/addresses'
import { getBridgeProviderType } from '~/utils/bridgeProvider'
import { getChainLabel } from '~/utils/chainLabels'
import { generateSalt } from '~/utils/crypto'
import { displayBigNumber } from '~/utils/display'
import { isVaultToken } from '~/utils/vault'

export const useCreateSmartAccounts = (): {
  setupSmartAccounts: ({
    treasuryPasskeyIds,
    operatingPasskeyIds,
    orgId,
  }: {
    treasuryPasskeyIds: string[]
    operatingPasskeyIds: string[]
    orgId: string
  }) => Promise<void>
  createSmartAccount: ({
    passkeyIds,
    orgId,
    name,
    threshold,
  }: {
    passkeyIds: string[]
    orgId: string
    name: string
    threshold: number
  }) => Promise<void>
  isProcessing: boolean
} => {
  const { serverPostRequest, isProcessing } = useServerRequests()
  const navigate = useNavigation()
  const queryClient = useQueryClient()

  const setupSmartAccounts = async ({
    treasuryPasskeyIds,
    operatingPasskeyIds,
    orgId,
  }: {
    treasuryPasskeyIds: string[]
    operatingPasskeyIds: string[]
    orgId: string
  }) => {
    const salt = generateSalt()

    const result = await serverPostRequest<{
      smartAccounts: OrgSmartAccount[]
    }>({
      path: '/api/smart_accounts/',
      params: {
        orgId,
        accounts: [
          {
            name: 'Treasury',
            passkeyIds: treasuryPasskeyIds,
            threshold: 2,
            salt,
          },
          {
            name: 'Operating',
            passkeyIds: operatingPasskeyIds,
            threshold: 1,
            salt,
          },
        ],
      },
      errorMessage: 'Failed to create smart accounts',
    })

    if (result) {
      toast.success('Smart accounts created')
      navigate(`/`)
    }
  }

  const createSmartAccount = async ({
    passkeyIds,
    orgId,
    name,
    threshold,
  }: {
    passkeyIds: string[]
    orgId: string
    name: string
    threshold: number
  }) => {
    const salt = generateSalt()

    const result = await serverPostRequest<{
      smartAccounts: OrgSmartAccount[]
    }>({
      path: '/api/smart_accounts/',
      params: {
        orgId,
        accounts: [
          {
            name,
            passkeyIds,
            threshold,
            salt,
          },
        ],
      },
      errorMessage: 'Failed to create smart account',
    })

    if (result) {
      toast.success('Smart account created')

      const { address } = result.data.smartAccounts[0]

      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, orgId],
      })
      navigate(`/accounts/${address}`)
    }
  }

  return {
    setupSmartAccounts,
    createSmartAccount,
    isProcessing,
  }
}

type CreateSwapTransactionParams = {
  transactionId: string | undefined
  data: SwapTransactionParams
  dontOpenDialog: boolean
  onSuccess?: (transaction: OrgTransaction) => void
}

export const useCreateSwapTransaction = (): {
  createSwapTransaction: (args: CreateSwapTransactionParams) => Promise<void>
  isProcessing: boolean
} => {
  const { org } = useActiveOrg()
  const { createTransaction, isProcessing } = useCreateTransaction()
  const queryClient = useQueryClient()

  const createSwapTransaction = useCallback(
    async ({
      transactionId,
      data,
      dontOpenDialog,
      onSuccess,
    }: CreateSwapTransactionParams) => {
      const transactionTypes = getTransactionTypes(data)
      const name = getTransactionName({
        data,
        transactionTypes,
        org,
        queryClient,
      })

      const swapData = {
        buyAmount: data.buyAmount.toString(),
        buyTokenAddress: data.buyToken.address,
        sellAmount: data.sellAmount.toString(),
        sellTokenAddress: data.sellToken.address,
        direction: data.direction,
        provider: data.provider,
        slippage: data.slippage,
        private: data.private,
      }

      const isBridgeTransaction = transactionTypes.includes(
        BasicTransactionType.Bridge,
      )

      if (isBridgeTransaction && !data.providerTransactionId) {
        throw new Error('Provider transaction ID is required')
      }

      // A provider transaction id exists exactly when a provider settles the
      // swap in a separate leg (cross-chain bridges, NearIntents even
      // same-chain). The backend verifies that settlement via the persisted
      // bridgeData, so it must be attached whenever there is an id — the
      // Bridge type tag stays cross-chain-only.
      const needsSettlementVerification = !!data.providerTransactionId

      const bridgeData = {
        destinationChainId: data.destinationChainId,
        sellAmount: data.sellAmount.toString(),
        sellTokenAddress: data.sellToken.address,
        providerTransactionId: data.providerTransactionId ?? '',
        providerType: getBridgeProviderType(
          data.settlementRouter ?? data.provider,
        ),
        private: data.private,
        buyTokenAddress: data.buyToken.address,
        buyAmount: data.buyAmount.toString(),
      }

      const transferData = {
        ...(data.recipients
          ? {
              recipients: data.recipients.map((recipient) => ({
                address: recipient.address,
                amount: parseUnits(
                  recipient.amount,
                  data.buyToken.decimals,
                ).toString(),
                compliantPaymentRelationId:
                  recipient.compliantPaymentRelationId,
              })),
            }
          : {
              // NOTE: This will only be reached in the SwapForm where the sender is the receiver
              recipients: [
                {
                  address: data.receiver,
                  amount: swapData.buyAmount.toString(),
                },
              ],
            }),
        tokenAddress: swapData.buyTokenAddress,
      }

      const earnData: EarnDataPayload | undefined = data.earnData
        ? {
            action: data.earnData.action,
            assets: data.earnData.amount.toString(),
            asset: data.earnData.token,
            vault:
              data.earnData.action === EarnAction.DEPOSIT
                ? data.buyToken.address
                : data.sellToken.address,
            shares:
              data.earnData.action === EarnAction.DEPOSIT
                ? data.buyAmount.toString()
                : data.sellAmount.toString(),
            chainId: data.chainId,
          }
        : undefined

      const bankAccountMatch = org?.bridge?.externalAccounts.find(
        ({ liquidationAddress }) =>
          Address.equal(data.receiver, liquidationAddress),
      )
      const isOfframp = bankAccountMatch !== undefined

      const transactionData: CreateTransactionData['transactionData'] = {}
      if (transactionTypes.includes(BasicTransactionType.Swap)) {
        transactionData.swapData = swapData
      }
      if (needsSettlementVerification) {
        transactionData.bridgeData = bridgeData
      }
      if (transactionTypes.includes(BasicTransactionType.Transfer)) {
        transactionData.transferData = transferData
      }
      if (transactionTypes.includes(BasicTransactionType.Earn)) {
        transactionData.earnData = earnData
      }

      const createTransactionData: CreateTransactionData = {
        chainId: data.chainId,
        name,
        smartAccountId: data.smartAccountId,
        smartAccountAddress: data.smartAccountAddress,
        calls: data.calls,
        onSuccess,
        memo: data.memo,
        isOfframp,
        types: transactionTypes,
        transactionData,
      }

      const loggingData = {
        isOfframp,
        ...(data.loggingData ? data.loggingData : {}),
      }
      await createTransaction({
        transactionId,
        txData: createTransactionData,
        dontOpenDialog,
        loggingData,
      })
    },
    [createTransaction, org, queryClient],
  )

  return {
    createSwapTransaction,
    isProcessing,
  }
}

const getTransactionTypes = (data: SwapTransactionParams) => {
  const types: BasicTransactionType[] = []

  const isBridge = data.destinationChainId !== data.chainId
  if (isBridge) {
    types.push(BasicTransactionType.Bridge)
  }

  const isEarn =
    isVaultToken(data.chainId, Address.format(data.sellToken.address)) ||
    isVaultToken(data.destinationChainId, Address.format(data.buyToken.address))
  if (isEarn) {
    types.push(BasicTransactionType.Earn)
  }

  const isSwap = data.buyToken.symbol !== data.sellToken.symbol
  if (isSwap) {
    types.push(BasicTransactionType.Swap)
  }

  const isTransfer =
    data.recipients?.some(
      (recipient) => recipient.address !== data.smartAccountAddress,
    ) || data.receiver !== data.smartAccountAddress
  if (isTransfer) {
    types.push(BasicTransactionType.Transfer)
  }

  return types
}

// TODO: implement for wallet and transfer type txns.
export const getTransactionName = ({
  data,
  transactionTypes,
  org,
  queryClient,
}: {
  data: SwapTransactionParams
  transactionTypes: BasicTransactionType[]
  org?: AuthOrg
  queryClient?: ReturnType<typeof useQueryClient>
}): string => {
  const bankAccountMatch = org?.bridge?.externalAccounts.find(
    ({ liquidationAddress }) =>
      Address.equal(data.receiver, liquidationAddress),
  )
  if (bankAccountMatch) {
    return `Withdraw ${displayBigNumber(data.buyAmount, {
      displayDecimals: 4,
      tokenDecimals: data.buyToken.decimals,
      minDisplayDecimals: 0,
    })} ${data.buyToken.symbol} to ${bankAccountMatch.name}`
  }

  let name = ''

  // Earn takes priority — display the underlying asset amount (USDC equivalent)
  // instead of vault token shares
  if (transactionTypes.includes(BasicTransactionType.Earn) && data.earnData) {
    const isDeposit = data.earnData.action === EarnAction.DEPOSIT
    const underlyingToken = isDeposit ? data.sellToken : data.buyToken

    if (isDeposit) {
      name = `Deposit${getTokenDisplayForTransactionName(
        data.earnData.amount,
        underlyingToken,
      )} into ${underlyingToken.symbol}`
    } else {
      name = `Withdraw${getTokenDisplayForTransactionName(
        data.earnData.amount,
        underlyingToken,
      )} from ${underlyingToken.symbol}`
    }
  }

  if (transactionTypes.includes(BasicTransactionType.Bridge)) {
    if (!name) {
      name += `Bridge ${getTokenDisplayForTransactionName(
        data.sellAmount,
        data.sellToken,
      )} to ${getChainLabel(data.destinationChainId, { queryClient: queryClient! })}`
    }
  }

  if (transactionTypes.includes(BasicTransactionType.Swap)) {
    if (!name) {
      name += `Swap ${getTokenDisplayForTransactionName(
        data.sellAmount,
        data.sellToken,
      )} for ${getTokenDisplayForTransactionName(
        data.buyAmount,
        data.buyToken,
      )}`
    } else if (!transactionTypes.includes(BasicTransactionType.Earn)) {
      if (transactionTypes.includes(BasicTransactionType.Transfer)) {
        name += `, `
      } else {
        name += ` and `
      }
      name += `swap for ${getTokenDisplayForTransactionName(
        data.buyAmount,
        data.buyToken,
      )}`
    }
  }

  if (transactionTypes.includes(BasicTransactionType.Transfer)) {
    if (!name) {
      name += `Transfer to ${shortenAddress(data.receiver)}`
    } else {
      name += ` and transfer to ${shortenAddress(data.receiver)}`
    }
  }

  if (!name) throw new Error(`Implementation Missing: ${transactionTypes}`)

  return name
}
